import { db } from './firebase.js';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import {
  CardInstance,
  CreateCardInstanceData,
  UpdateCardInstanceData,
  CollectionStats,
  CardCondition,
  CardVariant
} from '../types/collection.ts';

/**
 * Get the collection reference for a user's card instances
 */
function getCollectionRef(userId: string) {
  if (!db) throw new Error('Firebase not initialized');
  return collection(db, 'users', userId, 'collection');
}

/**
 * Add a new card instance to the user's collection
 */
export async function addCardInstance(
  userId: string,
  data: CreateCardInstanceData
): Promise<string> {
  const colRef = getCollectionRef(userId);

  const instance: Omit<CardInstance, 'id'> = {
    cardId: data.cardId,
    condition: data.condition,
    variant: data.variant,
    quantity: data.quantity,
    acquiredAt: serverTimestamp() as Timestamp,
    forTrade: data.forTrade ?? false,
    ...(data.acquiredPrice !== undefined && { acquiredPrice: data.acquiredPrice }),
    ...(data.notes && { notes: data.notes })
  };

  const docRef = await addDoc(colRef, instance);

  // Update user stats
  await updateCollectionStats(userId);

  return docRef.id;
}

/**
 * Get all card instances for a user
 */
export async function getCardInstances(userId: string): Promise<CardInstance[]> {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, orderBy('acquiredAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as CardInstance));
}

/**
 * Get card instances for a specific card ID
 */
export async function getCardInstancesByCardId(
  userId: string,
  cardId: string
): Promise<CardInstance[]> {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, where('cardId', '==', cardId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as CardInstance));
}

/**
 * Get a single card instance by ID
 */
export async function getCardInstance(
  userId: string,
  instanceId: string
): Promise<CardInstance | null> {
  if (!db) return null;
  const docRef = doc(db, 'users', userId, 'collection', instanceId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data()
  } as CardInstance;
}

/**
 * Update a card instance
 */
export async function updateCardInstance(
  userId: string,
  instanceId: string,
  data: UpdateCardInstanceData
): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');
  const docRef = doc(db, 'users', userId, 'collection', instanceId);
  await updateDoc(docRef, { ...data });
  await updateCollectionStats(userId);
}

/**
 * Delete a card instance
 */
export async function deleteCardInstance(
  userId: string,
  instanceId: string
): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');
  const docRef = doc(db, 'users', userId, 'collection', instanceId);
  await deleteDoc(docRef);
  await updateCollectionStats(userId);
}

/**
 * Get all unique card IDs in the collection (for compatibility with old system)
 */
export async function getOwnedCardIds(userId: string): Promise<Set<string>> {
  const instances = await getCardInstances(userId);
  return new Set(instances.map(i => i.cardId));
}

/**
 * Get cards marked for trade
 */
export async function getCardsForTrade(userId: string): Promise<CardInstance[]> {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, where('forTrade', '==', true));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as CardInstance));
}

/**
 * Toggle trade status for a card instance
 */
export async function toggleForTrade(
  userId: string,
  instanceId: string
): Promise<boolean> {
  const instance = await getCardInstance(userId, instanceId);
  if (!instance) throw new Error('Instance not found');

  const newStatus = !instance.forTrade;
  await updateCardInstance(userId, instanceId, { forTrade: newStatus });
  return newStatus;
}

/**
 * Batch add multiple card instances (useful for imports)
 */
export async function batchAddCardInstances(
  userId: string,
  cards: CreateCardInstanceData[]
): Promise<string[]> {
  if (!db) throw new Error('Firebase not initialized');

  const colRef = getCollectionRef(userId);
  const batch = writeBatch(db);
  const ids: string[] = [];

  // Firebase batch limit is 500
  const chunks = [];
  for (let i = 0; i < cards.length; i += 500) {
    chunks.push(cards.slice(i, i + 500));
  }

  for (const chunk of chunks) {
    for (const data of chunk) {
      const docRef = doc(colRef);
      ids.push(docRef.id);

      batch.set(docRef, {
        cardId: data.cardId,
        condition: data.condition,
        variant: data.variant,
        quantity: data.quantity,
        acquiredAt: serverTimestamp(),
        forTrade: data.forTrade ?? false,
        ...(data.acquiredPrice !== undefined && { acquiredPrice: data.acquiredPrice }),
        ...(data.notes && { notes: data.notes })
      });
    }
    await batch.commit();
  }

  await updateCollectionStats(userId);
  return ids;
}

/**
 * Get collection statistics
 */
export async function getCollectionStats(userId: string): Promise<CollectionStats> {
  const instances = await getCardInstances(userId);

  const uniqueCardIds = new Set(instances.map(i => i.cardId));
  const totalCards = instances.reduce((sum, i) => sum + i.quantity, 0);
  const cardsForTrade = instances.filter(i => i.forTrade).length;

  // TODO: Calculate real values from price data
  const totalValue = 0;
  const valueChange24h = 0;
  const valueChangePercent = 0;

  // TODO: Get wanted cards from wanted list
  const cardsWanted = 0;

  // TODO: Calculate set completion
  const setsStarted = 0;
  const setsCompleted = 0;

  return {
    uniqueCards: uniqueCardIds.size,
    totalCards,
    totalValue,
    valueChange24h,
    valueChangePercent,
    setsStarted,
    setsCompleted,
    cardsForTrade,
    cardsWanted
  };
}

/**
 * Update collection stats in user document
 */
async function updateCollectionStats(userId: string): Promise<void> {
  if (!db) return;

  try {
    const stats = await getCollectionStats(userId);
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'stats.uniqueCards': stats.uniqueCards,
      'stats.totalCards': stats.totalCards,
      'stats.cardsForTrade': stats.cardsForTrade,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Failed to update collection stats:', error);
  }
}

/**
 * Subscribe to collection changes (real-time updates)
 */
export function subscribeToCollection(
  userId: string,
  callback: (instances: CardInstance[]) => void
): Unsubscribe {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, orderBy('acquiredAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const instances = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CardInstance));
    callback(instances);
  });
}

/**
 * Get recently added cards
 */
export async function getRecentCards(
  userId: string,
  count: number = 10
): Promise<CardInstance[]> {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, orderBy('acquiredAt', 'desc'), limit(count));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as CardInstance));
}

/**
 * Check if a card ID exists in collection
 */
export async function hasCard(userId: string, cardId: string): Promise<boolean> {
  const colRef = getCollectionRef(userId);
  const q = query(colRef, where('cardId', '==', cardId), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Get total quantity of a specific card across all instances
 */
export async function getCardQuantity(userId: string, cardId: string): Promise<number> {
  const instances = await getCardInstancesByCardId(userId, cardId);
  return instances.reduce((sum, i) => sum + i.quantity, 0);
}

/**
 * Quick add a card with default values (for backward compatibility)
 */
export async function quickAddCard(
  userId: string,
  cardId: string,
  condition: CardCondition = 'near_mint',
  variant: CardVariant = 'normal'
): Promise<string> {
  return addCardInstance(userId, {
    cardId,
    condition,
    variant,
    quantity: 1,
    forTrade: false
  });
}

/**
 * Migrate from old collection format to new instance-based format
 */
export async function migrateFromLegacyCollection(
  userId: string,
  ownedCardIds: string[]
): Promise<number> {
  if (!db) throw new Error('Firebase not initialized');

  let migrated = 0;
  const existingIds = await getOwnedCardIds(userId);

  const toMigrate = ownedCardIds.filter(id => !existingIds.has(id));

  if (toMigrate.length === 0) return 0;

  const cardsToAdd: CreateCardInstanceData[] = toMigrate.map(cardId => ({
    cardId,
    condition: 'near_mint' as CardCondition,
    variant: 'normal' as CardVariant,
    quantity: 1,
    forTrade: false
  }));

  await batchAddCardInstances(userId, cardsToAdd);
  migrated = cardsToAdd.length;

  return migrated;
}
